/**
 * Top-level app with auth + routing.
 *
 *   /login     → login (or bootstrap when users table is empty)
 *   /account   → change password + PIN (all roles)
 *   /settings  → settings + user admin (owner only)
 *   /          → Dashboard (setup complete) or wizard (setup incomplete)
 *   /wizard    → setup wizard
 *   /batches*  → batches screens
 *   /ledgers   → ledgers index
 *   /parties/:id/ledger → party ledger
 */
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Routes, Route, Navigate, useNavigate, useLocation, Link } from 'react-router-dom';
import { api } from './api';
import type { WizardState, WhoamiResponse } from './api';
import { Spinner } from './ui/primitives';
import { DashboardPage } from './pages/DashboardPage';
import { WizardPage } from './pages/WizardPage';
import { BatchesPage } from './pages/BatchesPage';
import { BatchDetailPage } from './pages/BatchDetailPage';
import { NewBatchPage } from './pages/NewBatchPage';
import { LedgersIndexPage } from './pages/LedgersIndexPage';
import { PartyLedgerPage } from './pages/PartyLedgerPage';
import { SettingsPage } from './pages/SettingsPage';
import { LoginPage } from './pages/LoginPage';
import { AccountPage } from './pages/AccountPage';
import { AccountsManagePage } from './pages/AccountsManagePage';
import { UsersAdminPage } from './pages/UsersAdminPage';
import { useWhoami } from './lib/auth';
import { PinGateProvider } from './auth/pinGate';
import { MatchRouter } from './match';

export function App() {
  const whoami = useWhoami();

  if (whoami.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg text-ink-muted">
        <div className="flex items-center gap-2">
          <Spinner /> loading…
        </div>
      </div>
    );
  }
  if (whoami.error || !whoami.data) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-2 bg-bg text-ink p-6">
        <h1 className="text-lg font-semibold">Cannot reach the backend</h1>
        <p className="text-sm text-ink-muted">
          Make sure the accounts server is running. Refresh in a moment.
        </p>
      </div>
    );
  }

  // Unauthenticated / bootstrap → only login route is reachable.
  if (
    whoami.data.state === 'bootstrap_required' ||
    whoami.data.state === 'unauthenticated'
  ) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  // Authenticated: wrap everything in the PIN-gate provider so destructive
  // actions can trigger the modal.
  return (
    <PinGateProvider>
      <AuthenticatedApp who={whoami.data} />
    </PinGateProvider>
  );
}

function AuthenticatedApp({ who }: { who: Extract<WhoamiResponse, { state: 'authenticated' }> }) {
  const { data: wizard, isLoading, error } = useQuery<WizardState>({
    queryKey: ['wizard'],
    queryFn: () => api.get<WizardState>('/api/wizard'),
    refetchInterval: 5000,
  });

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg text-ink-muted">
        <div className="flex items-center gap-2">
          <Spinner /> loading…
        </div>
      </div>
    );
  }
  if (error || !wizard) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-2 bg-bg text-ink p-6">
        <h1 className="text-lg font-semibold">Cannot reach the backend</h1>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={<Navigate to="/" replace />} />
      <Route
        path="/"
        element={
          hasMin(wizard) ? (
            <SetupGate wizard={wizard}>
              <DashboardPage />
            </SetupGate>
          ) : (
            <Navigate to="/wizard" replace />
          )
        }
      />
      <Route path="/wizard" element={<WizardPage wizard={wizard} />} />
      <Route
        path="/batches"
        element={
          <SetupGate wizard={wizard}>
            <BatchesPage />
          </SetupGate>
        }
      />
      <Route
        path="/batches/new"
        element={
          <SetupGate wizard={wizard}>
            <NewBatchPage />
          </SetupGate>
        }
      />
      <Route
        path="/batches/:id"
        element={
          <SetupGate wizard={wizard}>
            <BatchDetailPage />
          </SetupGate>
        }
      />
      <Route
        path="/match/*"
        element={
          <SetupGate wizard={wizard}>
            <MatchRouter />
          </SetupGate>
        }
      />
      <Route
        path="/ledgers"
        element={
          <SetupGate wizard={wizard}>
            <LedgersIndexPage />
          </SetupGate>
        }
      />
      <Route
        path="/parties/:id/ledger"
        element={
          <SetupGate wizard={wizard}>
            <PartyLedgerPage />
          </SetupGate>
        }
      />
      <Route
        path="/settings"
        element={
          <SetupGate wizard={wizard}>
            <SettingsPage />
          </SetupGate>
        }
      />
      <Route
        path="/settings/users"
        element={
          <SetupGate wizard={wizard}>
            {who.role === 'owner' ? (
              <UsersAdminPage />
            ) : (
              <Navigate to="/" replace />
            )}
          </SetupGate>
        }
      />
      <Route
        path="/accounts"
        element={
          <SetupGate wizard={wizard}>
            <AccountsManagePage />
          </SetupGate>
        }
      />
      <Route path="/account" element={<AccountPage />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function hasMin(wizard: WizardState) {
  return (
    wizard.counts.selfParties >= 1 &&
    wizard.counts.uplineParties >= 1 &&
    wizard.counts.accounts >= 1
  );
}

function SetupGate({
  wizard,
  children,
}: {
  wizard: WizardState;
  children: React.ReactNode;
}) {
  const nav = useNavigate();
  const loc = useLocation();
  const ok = hasMin(wizard);
  useEffect(() => {
    if (!ok) nav('/wizard', { replace: true, state: { from: loc.pathname } });
  }, [ok, nav, loc.pathname]);
  return ok ? <>{children}</> : null;
}

function NotFound() {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-2 bg-bg text-ink p-6">
      <h1 className="text-lg font-semibold">Not found</h1>
      <Link to="/" className="text-brand underline">
        Go home
      </Link>
    </div>
  );
}
